import type { Placement } from "@floating-ui/react"
import { Link, type LinkProps, type RegisteredRouter } from "@tanstack/react-router"
import { clsx } from "clsx"
import {
  type ReactNode,
  type ButtonHTMLAttributes,
  type AnchorHTMLAttributes,
  Children,
  isValidElement,
  type ReactElement,
  type Ref,
} from "react"

import { Tooltip } from "./Tooltip"

const sizeStyles = {
  xsmall: {
    base: "h-7 text-xs font-normal",
    padding: "px-2.5",
    condensedPadding: "px-2",
    gap: "gap-1",
  },
  small: {
    base: "h-8 text-sm font-normal",
    padding: "px-3",
    condensedPadding: "px-2",
    gap: "gap-1.5",
  },
  medium: { base: "h-9 text-sm", padding: "px-4", condensedPadding: "px-3", gap: "gap-2" },
  large: { base: "h-12 text-base", padding: "px-6", condensedPadding: "px-4", gap: "gap-2.5" },
  icon: "size-9 shrink-0",
  "icon-small": "size-8 shrink-0",
  "icon-xsmall": "size-7 shrink-0",
}

const variantStyles = {
  outline:
    "border border-[rgb(0_0_0_/_0.12)] bg-transparent text-default hover:bg-hover active:bg-active data-[state=open]:not-active:bg-hover dark:border-[rgb(255_255_255_/_0.12)]",
  ghost:
    "bg-transparent text-default hover:bg-hover active:bg-active data-[state=open]:not-active:bg-hover border border-transparent",
  attention:
    "border border-[rgb(0_0_0_/_0.12)] bg-neutral-900 bg-clip-padding text-white hover:bg-neutral-800 active:bg-neutral-700 data-[state=open]:not-active:bg-neutral-800 dark:border-[rgb(255_255_255_/_0.12)] dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-300 dark:active:bg-neutral-400 dark:data-[state=open]:not-active:bg-neutral-300 focus-visible:[box-shadow:inset_0_0_0_4px_white] dark:focus-visible:[box-shadow:inset_0_0_0_4px_rgb(23_23_23)]",
  danger:
    "text-red-700 dark:text-red-400 hover:bg-red-700 dark:hover:bg-red-800 hover:text-white active:bg-red-800 data-[state=open]:not-active:bg-red-700 data-[state=open]:text-white dark:active:bg-red-900 dark:data-[state=open]:not-active:bg-red-800 border border-red-700 dark:border-red-600",
}

const baseStyles =
  "flex items-center justify-center rounded-lg font-medium transition-colors duration-150 focus:transition-none cursor-pointer min-w-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none focus:outline-2 focus:-outline-offset-2 focus:outline-transparent focus-visible:outline-focus"
const contentChildStyles = "[&>*:not([data-button-text])]:shrink-0"

type Size = keyof typeof sizeStyles
type Variant = keyof typeof variantStyles

interface BaseButtonProps {
  size?: Size
  variant?: Variant
  condensed?: boolean
  className?: string
  children: ReactNode
  disabled?: boolean
  tooltipText?: ReactNode
  tooltipRelationship?: "description" | "label"
  tooltipCloseOnClick?: boolean
  tooltipPosition?: Placement
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>
}

interface ButtonAsButton
  extends BaseButtonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> {
  href?: never
  to?: never
}

interface ButtonAsLink
  extends BaseButtonProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps> {
  href: string
  to?: never
}

type RouterLinkProps = LinkProps<"a", RegisteredRouter, string, string>

type ButtonAsRouterLink = BaseButtonProps &
  Omit<RouterLinkProps, keyof BaseButtonProps | "children" | "className"> & {
    to: string
    href?: never
    onClick?: AnchorHTMLAttributes<HTMLAnchorElement>["onClick"]
  }

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsRouterLink

export function Button({
  size = "medium",
  variant,
  condensed = false,
  className,
  children,
  disabled,
  tooltipText,
  tooltipRelationship,
  tooltipCloseOnClick,
  tooltipPosition,
  ref,
  ...rest
}: ButtonProps) {
  const sizeStyle = sizeStyles[size]
  const isIconButton = typeof sizeStyle === "string"
  const isDefaultButton = variant == null
  const hasShell = isDefaultButton || variant === "outline"

  const classes = clsx(
    baseStyles,
    isDefaultButton &&
      "floating-control-frame hover:before:bg-floating-hover active:before:bg-floating-active data-[state=open]:not-active:before:bg-floating-hover",
    isIconButton ? sizeStyle : sizeStyle.base,
    !isIconButton && "data-dropdown-trigger:justify-between data-dropdown-trigger:text-left",
    !isIconButton && !hasShell && sizeStyle.gap,
    !isIconButton && !hasShell && contentChildStyles,
    !isIconButton && !hasShell && !condensed && sizeStyle.padding,
    !isIconButton && !hasShell && condensed && sizeStyle.condensedPadding,
    variant ? variantStyles[variant] : "text-default",
    className,
  )

  // Check if children contains mixed content (icon/avatar + text)
  const childArray = Children.toArray(children)
  const hasMultipleChildren = childArray.length > 1
  const hasElementChild = childArray.some((child) => isValidElement(child))
  const isSingleTextChild =
    childArray.length <= 1 &&
    childArray.every((child) => typeof child === "string" || typeof child === "number")

  const content = isIconButton ? (
    children
  ) : hasMultipleChildren && hasElementChild ? (
    // Mixed content: wrap text nodes for truncation; CSS keeps SVG adornments fixed.
    <>
      {childArray.map((child, index) =>
        typeof child === "string" || typeof child === "number" ? (
          <span key={index} data-button-text className="min-w-0 truncate">
            {child}
          </span>
        ) : (
          child
        ),
      )}
    </>
  ) : isSingleTextChild ? (
    // Text-only button: wrap everything in truncate span
    <span data-button-text className="min-w-0 truncate">
      {children}
    </span>
  ) : (
    children
  )

  const renderedContent = hasShell ? (
    <span
      data-button-shell
      className={clsx(
        "flex size-full items-center [justify-content:inherit] rounded-[calc(var(--radius-lg)-1px)] [text-align:inherit] text-inherit transition-colors duration-150",
        !isIconButton && sizeStyle.gap,
        !isIconButton && contentChildStyles,
        !isIconButton && !condensed && sizeStyle.padding,
        !isIconButton && condensed && sizeStyle.condensedPadding,
      )}
    >
      {content}
    </span>
  ) : (
    content
  )

  function renderButtonTooltip(element: ReactElement, label: ReactNode) {
    const text = tooltipText ?? label
    if (!text) return element

    return (
      <Tooltip
        text={text}
        relationship={tooltipRelationship ?? (tooltipText === undefined ? "label" : "description")}
        position={tooltipPosition}
        closeOnClick={tooltipCloseOnClick}
      >
        {element}
      </Tooltip>
    )
  }

  // Router link
  if ("to" in rest && rest.to) {
    const routerLinkProps = rest as ButtonAsRouterLink
    const ariaLabel = routerLinkProps["aria-label" as keyof typeof routerLinkProps] as
      | string
      | undefined
    const link = (
      <Link
        {...(routerLinkProps as RouterLinkProps)}
        className={classes}
        aria-label={ariaLabel}
        aria-disabled={disabled}
      >
        {renderedContent}
      </Link>
    )

    if (isIconButton || tooltipText) {
      return renderButtonTooltip(link, ariaLabel)
    }

    return link
  }

  // External link
  if ("href" in rest && rest.href) {
    const linkProps = rest as ButtonAsLink
    const link = (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={linkProps.href}
        target={linkProps.target}
        rel={linkProps.rel}
        className={classes}
        aria-disabled={disabled}
        onClick={disabled ? undefined : linkProps.onClick}
        {...(linkProps as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {renderedContent}
      </a>
    )

    // Icon buttons derive a tooltip from their label; other buttons opt in explicitly.
    if (isIconButton || tooltipText) {
      return renderButtonTooltip(link, linkProps["aria-label"])
    }

    return link
  }

  // Button
  const buttonProps = rest as ButtonAsButton
  const button = (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={buttonProps.type || "button"}
      onClick={buttonProps.onClick}
      disabled={disabled}
      className={classes}
      {...(buttonProps as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {renderedContent}
    </button>
  )

  // Icon buttons derive a tooltip from their label; other buttons opt in explicitly.
  if (isIconButton || tooltipText) {
    return renderButtonTooltip(button, buttonProps["aria-label"])
  }

  return button
}
